import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './index.module.css';
import { executeQuery, executeMutation } from '../../graphql';
import { plateImageGenerator, combinationRecommender } from '../../graphql/mutations';
import { getVariantWithColourThemeAndPlatesByProductId, validatePlateNumber } from '../../graphql/queries';
import { preparePlateImageInput, getPlateImageDataUrl } from '../../utils/plateImageHelper';

const evaluateRule = (rule, input) => {
  const upper = input.toUpperCase();
  if (!upper.length) return rule.type === 'notStartWith' || rule.type === 'notRegex';
  switch (rule.type) {
    case 'exactLength': return upper.length === rule.count;
    case 'rangeLength': return upper.length >= rule.min && upper.length <= rule.max;
    case 'regex': return new RegExp(rule.pattern, 'i').test(upper);
    case 'notStartWith': return !upper.startsWith((rule.prefix || '').toUpperCase());
    case 'notRegex': return !(new RegExp(rule.pattern, 'i').test(upper));
    default: return true;
  }
};

const BuyNowComboContent = ({ modalProducts, productTitle, allProducts, c, onApplyAndContinue, onClose, initialProductIdx = 0 }) => {
  const [selectedProductIdx, setSelectedProductIdx] = useState(initialProductIdx);
  const [comboInput, setComboInput] = useState('');
  const [generatedPreviewImage, setGeneratedPreviewImage] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [isAvailable, setIsAvailable] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [recommendedCombos, setRecommendedCombos] = useState([]);
  const [recommendationImages, setRecommendationImages] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);

  useEffect(() => {
    setComboInput('');
    setGeneratedPreviewImage(null);
    setAvailabilityError('');
    setIsAvailable(null);
    setSelectedVariant(null);
    setRecommendedCombos([]);
    setRecommendationImages([]);
  }, [selectedProductIdx]);

  const selProd = modalProducts?.[selectedProductIdx] || null;

  useEffect(() => {
    if (!selProd) return;
    const rules = selProd.rules || [];
    const altRules = rules.filter(r => r.isAlternative);
    const nonAltRules = rules.filter(r => !r.isAlternative);
    const allPass = comboInput.length > 0 &&
      nonAltRules.every(r => evaluateRule(r, comboInput)) &&
      (altRules.length === 0 || altRules.some(r => evaluateRule(r, comboInput)));

    setAvailabilityError('');
    setIsAvailable(null);
    if (!allPass) {
      setGeneratedPreviewImage(null);
      return;
    }

    let cancelled = false;
    const fetchImage = async () => {
      try {
        const targetProduct = allProducts?.find(p => p.ProductName === selProd.productName);
        if (!targetProduct) return;

        const fullCombo = (selProd.comboPrefix || '') + comboInput;
        const variantsResult = await executeQuery(
          getVariantWithColourThemeAndPlatesByProductId,
          { ProductId: targetProduct.ProductId, CombinationType: fullCombo, SalesChannel: 1, IsDisplayBrowse: false }
        );
        if (cancelled) return;

        const variants = variantsResult?.getVariantByProductId;
        if (!variants?.length) return;

        const colourName = selProd.previewColourDisplayName;
        const variant = (colourName
          ? variants.find(v => v.ColourInfo?.DisplayName === colourName && v.SizeInfo?.SizeCode === 'ST')
            || variants.find(v => v.ColourInfo?.DisplayName === colourName)
          : null)
          || variants.find(v => v.SizeInfo?.SizeCode === 'ST')
          || variants[0];
        if (!cancelled) setSelectedVariant(variant);

        const input = preparePlateImageInput(variant, fullCombo);
        if (!input) return;
        const result = await executeMutation(plateImageGenerator, { input });
        if (!cancelled && result?.generatePlateImage) {
          setGeneratedPreviewImage(getPlateImageDataUrl(result.generatePlateImage));
        }
      } catch {
        if (!cancelled) setGeneratedPreviewImage(null);
      }
    };
    fetchImage();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboInput, selectedProductIdx]);

  useEffect(() => {
    if (!recommendedCombos.length || !selectedVariant) return;
    let cancelled = false;
    Promise.all(
      recommendedCombos.map(async (combo) => {
        try {
          const input = preparePlateImageInput(selectedVariant, combo);
          if (!input) return { combo, src: null };
          const result = await executeMutation(plateImageGenerator, { input });
          return { combo, src: result?.generatePlateImage ? getPlateImageDataUrl(result.generatePlateImage) : null };
        } catch {
          return { combo, src: null };
        }
      })
    ).then((images) => {
      if (!cancelled) setRecommendationImages(images.filter(img => img.src));
    });
    return () => { cancelled = true; };
  }, [recommendedCombos, selectedVariant]);

  const ruleStatuses = selProd
    ? (selProd.rules || []).map(rule => ({ ...rule, passes: evaluateRule(rule, comboInput) }))
    : [];
  const nonAltStatuses = ruleStatuses.filter(r => !r.isAlternative);
  const altStatuses = ruleStatuses.filter(r => r.isAlternative);
  const anyAltPasses = altStatuses.length === 0 || altStatuses.some(r => r.passes);
  const isComboValid = comboInput.length > 0 && nonAltStatuses.every(r => r.passes) && anyAltPasses;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComboValid || checkingAvailability) return;
    const fullCombo = (selProd?.comboPrefix || '') + comboInput;
    setCheckingAvailability(true);
    setAvailabilityError('');
    try {
      const result = await executeQuery(validatePlateNumber, {
        PlateCombo: fullCombo,
        VehicleType: selProd?.vehicleType ?? 1,
        SalesChannel: 1,
      });
      if (result?.getPlateRuleEngineStatus?.Status !== 1) {
        setAvailabilityError(c?.availabilityErrorText || 'This combo is not available');
        setIsAvailable(false);
        setRecommendedCombos([]);
        setRecommendationImages([]);
        setRecsLoading(true);
        executeMutation(combinationRecommender, {
          input: {
            plateNumber: fullCombo,
            groupName: selProd?.recommenderGroupName || '',
            vehicleType: (selProd?.vehicleType ?? 1) === 1 ? 'Car' : 'Motorbike',
            maxNumberOfRecommendations: 6,
          }
        })
          .then((recResult) => {
            const body = recResult?.recommender?.body;
            if (!body) return;
            const parsed = typeof body === 'string' ? JSON.parse(body) : body;
            const recs = parsed.recommendations || parsed.data || parsed || [];
            setRecommendedCombos(recs.slice(0, 6).map(r => r.PlateCombo || r.combo || r));
          })
          .catch(() => {})
          .finally(() => setRecsLoading(false));
        return;
      }
      setIsAvailable(true);
    } catch {
      setAvailabilityError(c?.availabilityVerifyErrorText || 'Unable to verify availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleReset = () => {
    setComboInput('');
    setIsAvailable(null);
    setAvailabilityError('');
    setGeneratedPreviewImage(null);
    setRecommendedCombos([]);
    setRecommendationImages([]);
  };

  return (
    <div className="modal-product-combo__body">
      {modalProducts?.length > 1 && (
        <fieldset className="modal-product-combo__product-selection">
          {modalProducts.map((mp, i) => (
            <div key={i} className="modal-product-combo__product-selection-option">
              <input
                type="radio"
                id={`combo-option-${i}`}
                name="combo-option"
                className="modal-product-combo__product-selection-input visuallyhidden visuallyhidden--no-focus"
                value={i}
                checked={selectedProductIdx === i}
                onChange={() => setSelectedProductIdx(i)}
              />
              <label htmlFor={`combo-option-${i}`} className="modal-product-combo__product-selection-label">
                {mp.label}
              </label>
            </div>
          ))}
        </fieldset>
      )}

      {(generatedPreviewImage || selProd?.previewImagePath) && (
        <img
          width="520"
          height="140"
          alt={`Preview image of a ${selProd?.label} plate`}
          src={generatedPreviewImage || selProd.previewImagePath}
          className="modal-product-combo__preview"
        />
      )}

      {selProd?.disclaimer && (
        <div className="modal-product-combo__disclaimer" dangerouslySetInnerHTML={{ __html: selProd.disclaimer }} />
      )}

      <form autoComplete="off" className="modal-product-combo__form" onSubmit={handleSubmit}>
        <div className="modal-product-combo__form-body">
          <label htmlFor="modal-product-combo-input" className="modal-product-combo__label">
            {c?.enterCombinationLabel || 'Enter a combination'}
          </label>

          {selProd?.patternsHtml && (
            <details className="modal-product-combo__patterns">
              <summary className="modal-product-combo__patterns-title">
                {c?.patternsAllowedLabel || 'Patterns allowed'}
              </summary>
              <div className="modal-product-combo__patterns-content" dangerouslySetInnerHTML={{ __html: selProd.patternsHtml }} />
            </details>
          )}

          <div className={`modal-product-combo__box${isAvailable === true ? ' modal-product-combo__box--success' : isAvailable === false ? ' modal-product-combo__box--failed' : ''}`}>
            {selProd?.comboPrefix && <div className="modal-product-combo__prefix">{selProd.comboPrefix}</div>}
            <input
              placeholder={selProd?.inputPlaceholder || ''}
              maxLength={selProd?.inputMaxLength || 10}
              id="modal-product-combo-input"
              name="modal-product-combo-input"
              required
              className="modal-product-combo__input"
              value={comboInput}
              onChange={(e) => setComboInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            />
          </div>

          <output name="combination-validity" htmlFor="modal-product-combo-input">
            {isAvailable === true ? (
              <div className="modal-product-combo__success-message" role="status">
                {c?.availableText || 'This combo is available!'}
              </div>
            ) : isAvailable === null ? (
              <>
                <h4 className="visuallyhidden">
                  {c?.rulesHeadingPrefix || 'Rules for'} {selProd?.label || productTitle} {c?.rulesHeadingSuffix || 'plates'}
                </h4>
                <ul className="modal-product-combo__rule-list">
                  {ruleStatuses.map((rule, i) => {
                    if (rule.onlyDisplayUponFail && rule.passes) return null;
                    const showAsFail = rule.isAlternative
                      ? (comboInput.length > 0 && !anyAltPasses)
                      : (comboInput.length > 0 || rule.onlyDisplayUponFail);
                    return (
                      <li key={i} className={`modal-product-combo__rule${rule.passes ? ' modal-product-combo__rule--success' : showAsFail ? ' modal-product-combo__rule--fail' : ''}`}>
                        <div className="modal-product-combo__rule-inner">
                          <div className="modal-product-combo__rule-message" dangerouslySetInnerHTML={{ __html: rule.message }} />
                          {rule.includeCharacterCount && (
                            <p className="modal-product-combo__count">
                              {comboInput.length}/{selProd?.inputMaxLength || 0}
                              <span className="visuallyhidden"> characters entered</span>
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </output>
        </div>

        {isAvailable === false && availabilityError && (
          <div className="modal-product-combo__failed-message" role="alert">{availabilityError}</div>
        )}

        <div className="modal-product-combo__button-list">
          {isAvailable === true ? (
            <>
              <button
                type="button"
                className="modal-product-combo__button"
                onClick={() => onApplyAndContinue?.((selProd?.comboPrefix || '') + comboInput, selProd)}
              >
                <span>{c?.applyAndContinueText || 'Apply and continue'}</span>
              </button>
              <button
                type="button"
                className="modal-product-combo__button modal-product-combo__button--reset"
                onClick={handleReset}
              >
                <span>{c?.resetComboText || 'Reset combo'}</span>
              </button>
            </>
          ) : isAvailable === false ? (
            <button type="button" className="modal-product-combo__button" onClick={handleReset}>
              <span>{c?.tryAnotherComboText || 'Try another combo'}</span>
            </button>
          ) : (
            <button
              type="submit"
              disabled={!isComboValid || checkingAvailability}
              className={`modal-product-combo__button${(!isComboValid || checkingAvailability) ? ' modal-product-combo__button--disabled' : ''}`}
            >
              <span>{checkingAvailability ? (c?.checkingText || 'Checking\u2026') : (c?.checkAvailabilityText || 'Check availability')}</span>
            </button>
          )}
        </div>
      </form>

      {isAvailable === false && (recsLoading || recommendationImages.length > 0) && (
        <div className="modal-product-combo__recommendations">
          <h3 className="modal-product-combo__recommendations-title">
            {c?.similarCombosTitle || 'Similar combinations available'}
          </h3>
          {recsLoading ? (
            <p className="modal-product-combo__recommendations-loading">
              {c?.recsLoadingText || 'Looking for similar combinations\u2026'}
            </p>
          ) : (
            <ul className="modal-product-combo__recommendations-list">
              {recommendationImages.map(({ combo, src }) => (
                <li key={combo} className="modal-product-combo__recommendations-item">
                  <button
                    type="button"
                    className={`modal-product-combo__recommendations-link ${styles.recommendationsLink}`}
                    onClick={() => {
                      onApplyAndContinue?.(combo, selProd);
                      onClose?.();
                    }}
                  >
                    <img
                      src={src}
                      alt={`Preview of plate with combination ${combo}`}
                      width="520"
                      height="140"
                      className="modal-product-combo__recommendations-image"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

BuyNowComboContent.propTypes = {
  modalProducts: PropTypes.array.isRequired,
  productTitle: PropTypes.string,
  allProducts: PropTypes.array,
  c: PropTypes.object,
  onApplyAndContinue: PropTypes.func,
  onClose: PropTypes.func,
  initialProductIdx: PropTypes.number,
};

export default BuyNowComboContent;
